import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, Upload, PencilSimple, Trash } from "@phosphor-icons/react";
import ItemFormDialog from "@/components/inventory/ItemFormDialog";

const CATEGORIES = [
  { v: "raw_material", l: "Raw Material" },
  { v: "packaging", l: "Packaging" },
  { v: "consumables", l: "Consumables" },
  { v: "finished_goods", l: "Finished Goods" },
  { v: "semi_finished", l: "Semi-Finished" },
];

export default function Items() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inventory/items", { params: { q, category: cat } });
      setRows(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load items");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [cat]);

  const onSubmit = async (form) => {
    try {
      if (editing) {
        await api.put(`/inventory/items/${editing.id}`, form);
        toast.success("Item updated");
      } else {
        await api.post("/inventory/items", form);
        toast.success("Item created");
      }
      setDialogOpen(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this item? This cannot be undone.")) return;
    try {
      await api.delete(`/inventory/items/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  const onImport = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/inventory/items/import", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.created} items (${data.skipped} skipped)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    }
  };

  return (
    <div className="space-y-5" data-testid="inv-items-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-600 font-semibold">
            Inventory · Master
          </div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Item Master</h1>
          <p className="text-sm text-slate-500 mt-1">
            Raw materials, packaging, consumables, finished goods.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx"
            className="hidden"
            onChange={(e) => onImport(e.target.files?.[0])}
            data-testid="items-import-file"
          />
          <button
            onClick={() => fileRef.current?.click()}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50"
            data-testid="items-import-btn"
          >
            <Upload size={14} weight="bold" /> Import Excel
          </button>
          <button
            onClick={() => { setEditing(null); setDialogOpen(true); }}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"
            data-testid="items-new-btn"
          >
            <Plus size={14} weight="bold" /> New Item
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search by name, code, barcode…"
            className="h-10 w-72 pl-9 pr-3 rounded-md border border-slate-200 bg-white text-sm"
            data-testid="items-search-input"
          />
        </div>
        <select
          value={cat}
          onChange={(e) => setCat(e.target.value)}
          className="h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
          data-testid="items-category-filter"
        >
          <option value="">All categories</option>
          {CATEGORIES.map((c) => (
            <option key={c.v} value={c.v}>{c.l}</option>
          ))}
        </select>
        <button
          onClick={load}
          className="h-10 px-4 rounded-md border border-slate-200 bg-white text-sm hover:bg-slate-50"
          data-testid="items-apply-filter"
        >
          Apply
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {["Code", "Name", "Category", "UoM", "Available", "Reorder", "Status", ""].map((h) => (
                <th
                  key={h}
                  className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-6 py-12 text-center text-sm text-slate-500">
                  No items yet. Click &quot;New Item&quot; or &quot;Import Excel&quot;.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`item-row-${r.id}`}>
                <td className="px-4 py-3 text-xs font-mono text-slate-700">{r.code}</td>
                <td className="px-4 py-3 text-sm font-medium text-slate-900">{r.name}</td>
                <td className="px-4 py-3 text-xs text-slate-600">{CATEGORIES.find((c) => c.v === r.category)?.l || r.category}</td>
                <td className="px-4 py-3 text-xs text-slate-600">{r.uom}</td>
                <td className="px-4 py-3 text-sm tabular-nums">{r.available_qty ?? 0}</td>
                <td className="px-4 py-3 text-xs text-slate-500 tabular-nums">{r.reorder_level || 0}</td>
                <td className="px-4 py-3">
                  <StatusBadge status={r.status} />
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    <button
                      onClick={() => { setEditing(r); setDialogOpen(true); }}
                      className="p-2 text-slate-500 hover:bg-slate-100 rounded"
                      data-testid={`item-edit-${r.id}`}
                    >
                      <PencilSimple size={14} />
                    </button>
                    <button
                      onClick={() => onDelete(r.id)}
                      className="p-2 text-rose-500 hover:bg-rose-50 rounded"
                      data-testid={`item-delete-${r.id}`}
                    >
                      <Trash size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ItemFormDialog
        open={dialogOpen}
        editing={editing}
        onClose={() => { setDialogOpen(false); setEditing(null); }}
        onSubmit={onSubmit}
      />
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    approved: "bg-emerald-50 text-emerald-700",
    draft: "bg-slate-100 text-slate-600",
    blocked: "bg-rose-50 text-rose-700",
    discontinued: "bg-amber-50 text-amber-700",
    archived: "bg-slate-100 text-slate-500",
  };
  return (
    <span className={`text-[10px] uppercase tracking-[0.16em] font-semibold px-2 py-1 rounded ${map[status] || "bg-slate-100 text-slate-600"}`}>
      {status || "—"}
    </span>
  );
}
